/**
 * Hub & spoke link catalog — single source of truth for the
 * flat hub-and-spoke internal-linking model defined in the SEO spec.
 *
 * Used by RelatedPagesGrid callers, hub pages, and intersection pages
 * to pull canonical labels with VARIED anchor text (rule #5: never repeat
 * the same anchor text twice on the same page).
 *
 * Pattern: every hub/spoke exposes 2-3 anchor variants. Callers pick the
 * variant they need so a single page can link to e.g. /colorado-springs-seo
 * from multiple sections without exact-match repetition.
 */

export type HubSpokeEntry = {
  to: string;
  /** Canonical card title for RelatedPagesGrid. */
  title: string;
  /** One-line description used in card grids. */
  description: string;
  /** Inline anchor variants — pick one per surface, never repeat on same page. */
  anchors: string[];
};

// ---------- TYPE A — SERVICE HUBS ----------
export const SERVICE_HUBS: Record<string, HubSpokeEntry> = {
  seo: {
    to: "/colorado-springs-seo",
    title: "Colorado SEO services",
    description:
      "Local + statewide SEO that ranks Colorado businesses for the searches their customers actually use.",
    anchors: [
      "Colorado SEO services",
      "our SEO team",
      "search engine optimization in Colorado",
      "local SEO for Colorado businesses",
    ],
  },
  webDesign: {
    to: "/colorado-springs-web-design",
    title: "Colorado web design services",
    description:
      "Hand-coded, mobile-first websites engineered to convert local Colorado visitors into booked jobs.",
    anchors: [
      "Colorado web design services",
      "our web design process",
      "custom Colorado website design",
      "fast Colorado business websites",
    ],
  },
  localSeo: {
    to: "/colorado-springs-google-maps",
    title: "Google Maps & local SEO",
    description:
      "Map Pack ranking, GBP optimization, and review velocity built for Colorado service areas.",
    anchors: [
      "Google Maps & local SEO",
      "local SEO services",
      "Google Business Profile optimization",
      "Colorado Map Pack ranking",
    ],
  },
  ppc: {
    to: "/colorado-springs-ppc",
    title: "Google Ads management",
    description:
      "Profitable paid search built and managed in-house — transparent reporting, zero contracts.",
    anchors: [
      "Google Ads management",
      "PPC for Colorado businesses",
      "paid search campaigns",
      "Google Ads & LSA management",
    ],
  },
  geoAi: {
    to: "/geo-ai",
    title: "GEO AI optimization",
    description:
      "Get cited when Coloradans ask ChatGPT, Gemini, Perplexity, or Google AI Overviews for a recommendation.",
    anchors: [
      "GEO AI optimization",
      "Generative Engine Optimization",
      "AI search visibility",
      "ranking in ChatGPT and Gemini",
    ],
  },
  digital: {
    to: "/digital-marketing",
    title: "Colorado digital marketing",
    description:
      "Full-stack digital marketing — SEO, web, ads, and content — for Colorado service businesses.",
    anchors: [
      "Colorado digital marketing",
      "full-service digital marketing",
      "Colorado marketing strategy",
      "integrated growth marketing",
    ],
  },
};

// ---------- TYPE B — CITY HUBS ----------
export const CITY_HUBS: Record<string, HubSpokeEntry> = {
  coloradoSprings: {
    to: "/digital-marketing",
    title: "Colorado Springs digital marketing agency",
    description:
      "Owner-led digital marketing for Colorado Springs businesses — SEO, web design, Google Maps, PPC, and GEO AI from our local HQ.",
    anchors: [
      "Colorado Springs digital marketing agency",
      "digital marketing in Colorado Springs",
      "Colorado Springs SEO company",
      "our Colorado Springs team",
      "Colorado Web Impressions in Colorado Springs",
    ],
  },
  denver: {
    to: "/denver-digital-marketing",
    title: "Denver digital marketing",
    description:
      "Cut through Colorado's most competitive market — SEO, web, ads, and AI search for Denver businesses.",
    anchors: ["Denver digital marketing", "digital marketing in Denver", "Denver SEO agency"],
  },
  fortCollins: {
    to: "/fort-collins-digital-marketing",
    title: "Fort Collins digital marketing",
    description:
      "Northern Colorado SEO, web, and ads for Fort Collins businesses serving the front range.",
    anchors: ["Fort Collins digital marketing", "digital marketing in Fort Collins", "Fort Collins marketing agency"],
  },
  boulder: {
    to: "/boulder-digital-marketing",
    title: "Boulder digital marketing",
    description:
      "Boulder-tuned SEO, web design, and AI search optimization for local and tech-adjacent businesses.",
    anchors: ["Boulder digital marketing", "digital marketing in Boulder", "Boulder SEO services"],
  },
  pueblo: {
    to: "/pueblo-digital-marketing",
    title: "Pueblo digital marketing",
    description:
      "Owner-led marketing for Pueblo trades and local businesses — practical SEO and web that ring the phone.",
    anchors: ["Pueblo digital marketing", "digital marketing in Pueblo", "Pueblo SEO agency"],
  },
  grandJunction: {
    to: "/grand-junction-digital-marketing",
    title: "Grand Junction digital marketing",
    description:
      "Western Slope SEO, web, and ads for Grand Junction businesses — local-first, no national filler.",
    anchors: ["Grand Junction digital marketing", "digital marketing in Grand Junction", "Grand Junction SEO"],
  },
  aurora: {
    to: "/aurora-digital-marketing",
    title: "Aurora digital marketing",
    description:
      "SEO, web, and Google Ads built for Aurora's diverse service-area landscape across the Denver metro.",
    anchors: ["Aurora digital marketing", "digital marketing in Aurora", "Aurora marketing agency"],
  },
  greeley: {
    to: "/greeley-digital-marketing",
    title: "Greeley digital marketing",
    description:
      "Greeley-tuned local SEO, web design, and ad management for Weld County trades and local businesses.",
    anchors: ["Greeley digital marketing", "digital marketing in Greeley", "Greeley SEO agency"],
  },
};

// ---------- TYPE C — INDUSTRY HUBS ----------
export const INDUSTRY_HUBS: Record<string, HubSpokeEntry> = {
  hvac: {
    to: "/hvac-marketing-colorado",
    title: "HVAC marketing Colorado",
    description:
      "Fill the schedule year-round — SEO, Google Maps, web, and ads for Colorado HVAC companies.",
    anchors: [
      "HVAC marketing Colorado",
      "SEO for HVAC companies in Colorado",
      "Colorado HVAC digital marketing",
      "HVAC lead generation Colorado",
    ],
  },
  plumbing: {
    to: "/plumbing-marketing-colorado",
    title: "Plumbing marketing Colorado",
    description:
      "Emergency-ready local SEO, web, and ads for Colorado plumbing companies.",
    anchors: [
      "Plumbing marketing Colorado",
      "SEO for Colorado plumbers",
      "plumbing lead generation Colorado",
      "Colorado plumbing digital marketing",
    ],
  },
  roofing: {
    to: "/roofing-seo-colorado",
    title: "Roofing SEO Colorado",
    description:
      "Storm-season-ready SEO, Google Maps, and ads for Colorado roofing companies.",
    anchors: [
      "Roofing SEO Colorado",
      "marketing for Colorado roofers",
      "Colorado roofing lead generation",
      "storm season roofing SEO",
    ],
  },
  electrical: {
    to: "/electrical-contractor-marketing-colorado",
    title: "Electrical contractor marketing Colorado",
    description:
      "SEO, Maps, and web for Colorado electrical contractors — including EV charger installs.",
    anchors: [
      "Electrical contractor marketing Colorado",
      "marketing for Colorado electricians",
      "electrician SEO Colorado",
    ],
  },
  landscaping: {
    to: "/landscaping-seo-colorado",
    title: "Landscaping SEO Colorado",
    description:
      "Seasonal local SEO and web for Colorado landscape, lawn, and tree-service companies.",
    anchors: [
      "Landscaping SEO Colorado",
      "marketing for Colorado landscapers",
      "Colorado lawn care SEO",
    ],
  },
  contractor: {
    to: "/contractor-web-design-colorado",
    title: "Contractor web design Colorado",
    description:
      "Mobile-first, conversion-focused websites for Colorado general and specialty contractors.",
    anchors: [
      "Contractor web design Colorado",
      "websites for Colorado contractors",
      "general contractor web design",
    ],
  },
  lawFirm: {
    to: "/law-firm-seo-colorado",
    title: "Law firm SEO Colorado",
    description:
      "Practice-area SEO and intake-focused web for Colorado attorneys and firms.",
    anchors: [
      "Law firm SEO Colorado",
      "marketing for Colorado attorneys",
      "Colorado law firm digital marketing",
    ],
  },
  dental: {
    to: "/dental-marketing-colorado",
    title: "Dental marketing Colorado",
    description:
      "New-patient SEO, GBP, and web for Colorado dental and orthodontic practices.",
    anchors: [
      "Dental marketing Colorado",
      "marketing for Colorado dentists",
      "Colorado dental practice SEO",
    ],
  },
};

// ---------- CITY SPOKES (Colorado Springs only) ----------
export const CS_SPOKES: Record<string, HubSpokeEntry> = {
  seo: {
    to: "/colorado-springs-seo",
    title: "Colorado Springs SEO",
    description:
      "Map Pack rankings and on-page SEO for Springs trades, healthcare, and pro-services.",
    anchors: ["Colorado Springs SEO", "SEO in Colorado Springs", "our Colorado Springs SEO team"],
  },
  webDesign: {
    to: "/colorado-springs-web-design",
    title: "Colorado Springs web design",
    description:
      "Fast, mobile-first websites built around how Springs customers actually buy.",
    anchors: ["Colorado Springs web design", "web design in Colorado Springs", "Springs business websites"],
  },
  googleMaps: {
    to: "/colorado-springs-google-maps",
    title: "Colorado Springs Google Maps",
    description:
      "Top-3 Map Pack visibility for Briargate, Falcon, Monument, and Black Forest.",
    anchors: ["Colorado Springs Google Maps", "Google Maps in Colorado Springs", "Springs Map Pack ranking"],
  },
  ppc: {
    to: "/colorado-springs-ppc",
    title: "Colorado Springs PPC",
    description:
      "Profitable Google Ads tuned to your service area, season, and capacity.",
    anchors: ["Colorado Springs PPC", "Google Ads in Colorado Springs", "Springs paid search"],
  },
  geoAi: {
    to: "/colorado-springs-geo-ai",
    title: "Colorado Springs GEO AI",
    description:
      "Get cited when locals ask ChatGPT, Gemini, or Perplexity for a Springs business.",
    anchors: ["Colorado Springs GEO AI", "AI search in Colorado Springs", "Springs GEO optimization"],
  },
};

// ---------- INDUSTRY SPOKES ----------
export const HVAC_SPOKES: Record<string, HubSpokeEntry> = {
  seo: {
    to: "/hvac-seo-colorado",
    title: "HVAC SEO in Colorado",
    description:
      "Rank for AC repair, furnace install, and emergency HVAC searches across Colorado.",
    anchors: ["HVAC SEO in Colorado", "SEO for HVAC contractors", "Colorado HVAC SEO"],
  },
  webDesign: {
    to: "/hvac-web-design-colorado",
    title: "HVAC web design in Colorado",
    description:
      "Hand-coded HVAC sites built to convert mobile emergency callers across Colorado.",
    anchors: ["HVAC web design in Colorado", "websites for Colorado HVAC", "HVAC site design"],
  },
  googleMaps: {
    to: "/hvac-google-maps-colorado",
    title: "HVAC Google Maps in Colorado",
    description:
      "Map Pack optimization for Colorado HVAC companies — GBP, reviews, and service tuning.",
    anchors: ["HVAC Google Maps in Colorado", "GBP for Colorado HVAC", "HVAC Map Pack ranking"],
  },
};

export const PLUMBING_SPOKES: Record<string, HubSpokeEntry> = {
  seo: {
    to: "/plumbing-seo-colorado",
    title: "Plumbing SEO in Colorado",
    description:
      "Rank for emergency plumber, water heater, and drain cleaning searches across Colorado.",
    anchors: ["Plumbing SEO in Colorado", "SEO for Colorado plumbers", "plumbing search ranking"],
  },
  webDesign: {
    to: "/plumbing-web-design-colorado",
    title: "Plumbing web design in Colorado",
    description:
      "Hand-coded plumbing sites built for late-night emergency calls on mobile.",
    anchors: ["Plumbing web design in Colorado", "websites for Colorado plumbers", "plumber site design"],
  },
  googleMaps: {
    to: "/plumbing-google-maps-colorado",
    title: "Plumbing Google Maps in Colorado",
    description:
      "Map Pack ranking and GBP optimization for Colorado plumbing companies.",
    anchors: ["Plumbing Google Maps in Colorado", "GBP for Colorado plumbers", "plumbing Map Pack ranking"],
  },
};

export const ROOFING_SPOKES: Record<string, HubSpokeEntry> = {
  webDesign: {
    to: "/roofing-web-design-colorado",
    title: "Roofing web design in Colorado",
    description:
      "Hand-coded roofing sites built for hail-damage traffic spikes and insurance-claim flow.",
    anchors: ["Roofing web design in Colorado", "websites for Colorado roofers", "roofer site design"],
  },
  googleMaps: {
    to: "/roofing-google-maps-colorado",
    title: "Roofing Google Maps in Colorado",
    description:
      "Map Pack ranking for Colorado roofers — storm-ready GBP optimization and review velocity.",
    anchors: ["Roofing Google Maps in Colorado", "GBP for Colorado roofers", "roofing Map Pack ranking"],
  },
  ppc: {
    to: "/roofing-ppc-colorado",
    title: "Roofing PPC in Colorado",
    description:
      "Storm-season Google Ads and LSAs built to capture insurance-claim leads.",
    anchors: ["Roofing PPC in Colorado", "Google Ads for Colorado roofers", "storm season roofing ads"],
  },
};

export const ELECTRICAL_SPOKES: Record<string, HubSpokeEntry> = {
  seo: {
    to: "/electrical-seo-colorado",
    title: "Electrical SEO in Colorado",
    description:
      "Rank for panel upgrades, EV-charger installs, and emergency electrical work across Colorado.",
    anchors: ["Electrical SEO in Colorado", "SEO for Colorado electricians", "electrician search ranking"],
  },
  googleMaps: {
    to: "/electrical-google-maps-colorado",
    title: "Electrical Google Maps in Colorado",
    description:
      "Map Pack ranking for Colorado electricians — GBP optimization and review velocity.",
    anchors: ["Electrical Google Maps in Colorado", "GBP for Colorado electricians", "electrician Map Pack"],
  },
};

export const LAW_FIRM_SPOKES: Record<string, HubSpokeEntry> = {
  googleMaps: {
    to: "/law-firm-google-maps-colorado",
    title: "Law firm Google Maps in Colorado",
    description:
      "Map Pack ranking and GBP optimization for Colorado law firms.",
    anchors: ["Law firm Google Maps in Colorado", "GBP for Colorado attorneys", "law firm Map Pack"],
  },
  ppc: {
    to: "/law-firm-ppc-colorado",
    title: "Law firm PPC in Colorado",
    description:
      "Practice-area Google Ads and LSA for Colorado attorneys — high-intent intake leads.",
    anchors: ["Law firm PPC in Colorado", "Google Ads for Colorado attorneys", "attorney paid search"],
  },
};

export const DENTAL_SPOKES: Record<string, HubSpokeEntry> = {
  seo: {
    to: "/dental-seo-colorado",
    title: "Dental SEO in Colorado",
    description:
      "Rank for new-patient searches across Colorado — implants, ortho, family dental, and more.",
    anchors: ["Dental SEO in Colorado", "SEO for Colorado dentists", "dental practice SEO"],
  },
  googleMaps: {
    to: "/dental-google-maps-colorado",
    title: "Dental Google Maps in Colorado",
    description:
      "Map Pack ranking and GBP optimization for Colorado dental and orthodontic practices.",
    anchors: ["Dental Google Maps in Colorado", "GBP for Colorado dentists", "dental Map Pack"],
  },
};

/** Helper — get a single anchor variant deterministically by index. */
export function pickAnchor(entry: HubSpokeEntry, idx: number): string {
  return entry.anchors[idx % entry.anchors.length] ?? entry.title;
}
